import { usePermissions } from '@/hooks/use-permissions';
import { PURCHASING_MODULE_KEY, REQUISITION_MODULE_KEYS } from '@/lib/purchasing';
import type { ModuleKey, ModuleRoleValue } from '@/lib/types';

/**
 * Requisition permission contract — mirrors
 * ``backend/procurement/viewsets.py`` (CanRaiseRequisition /
 * CanReviewRequisition / CanConvertRequisition). Change both together.
 *
 *   canRaise   → org Admin/Manager, OR supervisor-or-higher on ANY key in
 *                REQUISITION_MODULE_KEYS
 *   canReview  → org Admin/Manager, OR manager-or-higher on ANY key in
 *                REQUISITION_MODULE_KEYS
 *   canConvert → org Admin/Manager, OR manager-or-higher on
 *                PURCHASING_MODULE_KEY specifically
 *
 * Self-approval: only an org Admin may approve a requisition they raised,
 * and only after an explicit confirmation; the approval is audit-logged as
 * self-approved. Everyone below Admin is refused (hide Approve for them).
 *
 * Inspector shares Supervisor's numeric level but is deliberately excluded,
 * so roles are compared by name rather than level.
 *
 * Known gap (TD-161): per-user UserCapability overrides are not exposed to
 * the frontend. The API remains the source of truth.
 */
const RAISE_ROLES: ReadonlySet<ModuleRoleValue> = new Set(['supervisor', 'manager', 'admin']);
const REVIEW_ROLES: ReadonlySet<ModuleRoleValue> = new Set(['manager', 'admin']);

export function useRequisitionPermissions() {
  const permissions = usePermissions();
  const orgLevel = permissions.isAdmin || permissions.isManager;
  const keys = REQUISITION_MODULE_KEYS as ModuleKey[];

  const roleIn = (key: ModuleKey, allowed: ReadonlySet<ModuleRoleValue>) =>
    allowed.has(permissions.getModuleRole(key));

  const canRaise = orgLevel || keys.some((key) => roleIn(key, RAISE_ROLES));
  const canReview = orgLevel || keys.some((key) => roleIn(key, REVIEW_ROLES));
  const canConvert = orgLevel || roleIn(PURCHASING_MODULE_KEY as ModuleKey, REVIEW_ROLES);

  return { canRaise, canReview, canConvert };
}
